import {
  flatTumourTypeOptions,
  optionPairs,
  prostateDefaultSpecimens,
  translateOptions
} from './reportTemplateOptions';

export function createSharedProformaReport() {
  return {
    clinical: {
      history: '',
      specimenSites: [],
      specimenSiteOther: '',
      operativeProcedure: 'not-specified',
      operativeProcedureOther: ''
    },
    macroscopic: {
      description: '',
      measurement: {
        items: [createMeasurementItem()]
      },
      weight: {
        status: 'not-weighed',
        value: ''
      }
    },
    microscopic: {
      tumourType: '',
      tumourTypeOther: '',
      tumourTypeDetail: ''
    },
    otherFindings: {
      selected: [],
      other: ''
    },
    finalInterpretation: ''
  };
}

export function createMeasurementItem() {
  return {
    length: '',
    width: '',
    height: '',
    note: ''
  };
}

export function createGenericReport() {
  return {
    clinicalInformation: '',
    macroscopicFinding: '',
    microscopicFinding: '',
    diagnosis: '',
    comment: '',
    finalInterpretation: ''
  };
}

export function createBladderReport() {
  return {
    ...createSharedProformaReport(),
    bladder: {
      variantStatus: '',
      variantSubtypes: [],
      variantSubtypeOther: '',
      variantPercentage: '',
      urothelialGrade: '',
      growthPattern: '',
      sccAdenocarcinomaGrade: '',
      sccAdenocarcinomaGradeOther: '',
      depthOfInvasion: '',
      concomitantFlatLesion: '',
      muscularisPropria: {
        presence: '',
        invasion: ''
      }
    }
  };
}

export function createProstateReport() {
  return {
    clinical: {
      description: '',
      dre: '',
      psa: '',
      trus: ''
    },
    prostate: {
      specimens: prostateDefaultSpecimens.map(createProstateSpecimen),
      highGradePin: '',
      highGradePinDetail: '',
      idcp: '',
      idcpDetail: '',
      inflammation: '',
      periprostaticFatInvasion: '',
      seminalVesicleInvasion: '',
      lymphovascularInvasion: '',
      perineuralInvasion: ''
    },
    finalInterpretation: ''
  };
}

export function createProstateSpecimen(specimen = {}) {
  return {
    id: specimen.id || `custom-${Date.now()}`,
    code: specimen.code || '',
    labelKey: specimen.labelKey || '',
    customLabel: specimen.customLabel || '',
    selected: specimen.selected || false,
    coreCount: '',
    coreLengths: '',
    block: '1',
    result: '',
    resultOther: '',
    positiveCores: '',
    tumourVolume: '',
    gleasonScore: '',
    gradeGroup: '',
    cribriform: ''
  };
}

export function gradeGroupForGleason(value) {
  const mapping = {
    '3+3': 'grade-group-1',
    '3+4': 'grade-group-2',
    '4+3': 'grade-group-3',
    '4+4': 'grade-group-4',
    '3+5': 'grade-group-4',
    '5+3': 'grade-group-4',
    '4+5': 'grade-group-5',
    '5+4': 'grade-group-5',
    '5+5': 'grade-group-5'
  };
  return mapping[value] || '';
}

export function displayValue($t, value) {
  return value || $t('not-available');
}

export function displayOption($t, value, options) {
  const selected = options.find(option => option.value === value);
  return selected ? selected.label : displayValue($t, value);
}

export function displayOptionList($t, values, options) {
  if (!values || !values.length) {
    return $t('not-available');
  }
  return values.map(value => displayOption($t, value, options)).join(', ');
}

export function displayPercent($t, value) {
  return value === '' || value === null || value === undefined ? $t('not-available') : `${value}%`;
}

export function displayWithUnit($t, value, unit) {
  return value === '' || value === null || value === undefined ? $t('not-available') : `${value} ${unit}`;
}

export function buildGenericReportText(report, $t, diseaseLabel) {
  return [
    `${$t('report-disease-type')}: ${displayValue($t, diseaseLabel)}`,
    '',
    `[${$t('clinical-information-section')}]`,
    displayValue($t, report.clinicalInformation),
    '',
    `[${$t('macroscopic-finding-section')}]`,
    displayValue($t, report.macroscopicFinding),
    '',
    `[${$t('microscopic-finding-section')}]`,
    displayValue($t, report.microscopicFinding),
    '',
    `[${$t('generic-diagnosis-section')}]`,
    displayValue($t, report.diagnosis),
    '',
    `[${$t('generic-comment-section')}]`,
    displayValue($t, report.comment),
    '',
    `[${$t('final-interpretation-section')}]`,
    displayValue($t, report.finalInterpretation)
  ].join('\n');
}

export function buildSharedProformaLines(report, $t) {
  return [
    `[${$t('clinical-information-section')}]`,
    `${$t('history-previous-urinary-disease-therapy-ce')}: ${displayValue($t, report.clinical.history)}`,
    `${$t('specimen-site-proforma')}: ${displayOptionList($t, report.clinical.specimenSites, translateOptions($t, optionPairs.specimenSite))}${report.clinical.specimenSites.includes('other') ? ` - ${displayValue($t, report.clinical.specimenSiteOther)}` : ''}`,
    `${$t('operative-procedure-proforma')}: ${displayOption($t, report.clinical.operativeProcedure, translateOptions($t, optionPairs.operativeProcedure))}${report.clinical.operativeProcedure === 'other' ? ` - ${displayValue($t, report.clinical.operativeProcedureOther)}` : ''}`,
    '',
    `[${$t('macroscopic-finding-section')}]`,
    `${$t('submitted-tissue-description')}: ${displayValue($t, report.macroscopic.description)}`,
    `${$t('measurement')}: ${formatMeasurement(report, $t)}`,
    `${$t('weight')}: ${formatWeight(report, $t)}`,
    '',
    `[${$t('microscopic-finding-section')}]`,
    `${$t('histological-tumour-type')}: ${displayOption($t, getTumourType(report), flatTumourTypeOptions($t))}${getTumourType(report) === 'other' ? ` - ${displayValue($t, report.microscopic.tumourTypeOther)}` : ''}${getTumourType(report) === 'benign-condition' && report.microscopic.tumourTypeDetail ? `; ${$t('benign-condition-detail')}: ${report.microscopic.tumourTypeDetail}` : ''}`
  ];
}

export function buildCommonEndingLines(report, $t, suggestedFinalInterpretation = '') {
  const lines = [
    '',
    `[${$t('other-finding-section')}]`,
    `${$t('other-finding')}: ${displayOptionList($t, report.otherFindings.selected, translateOptions($t, optionPairs.otherFinding))}`
  ];
  if (report.otherFindings.selected.includes('other')) {
    lines.push(`${$t('other-specify')}: ${displayValue($t, report.otherFindings.other)}`);
  }
  lines.push(
    '',
    `[${$t('final-interpretation-section')}]`,
    displayValue($t, report.finalInterpretation || suggestedFinalInterpretation)
  );
  return lines;
}

export function buildBladderReportText(report, $t, diseaseLabel) {
  const tumourType = getTumourType(report);
  const hasUrothelialCarcinoma = tumourType === 'urothelial-carcinoma';
  const hasSccOrAdenocarcinoma = [
    'squamous-cell-carcinoma',
    'adenocarcinoma',
    'clear-cell-carcinoma',
    'endometrioid-carcinoma'
  ].includes(tumourType);
  const hasBladderGrading = hasUrothelialCarcinoma || hasSccOrAdenocarcinoma;
  const hasTumourAssessment = Boolean(tumourType && tumourType !== 'benign-condition');
  const bladderLines = [
    `${$t('report-disease-type')}: ${displayValue($t, diseaseLabel)}`,
    '',
    ...buildSharedProformaLines(report, $t)
  ];

  if (hasUrothelialCarcinoma) {
    bladderLines.push(`${$t('urothelial-variant-status')}: ${displayOption($t, report.bladder.variantStatus, translateOptions($t, optionPairs.variantStatus))}`);
    if (report.bladder.variantStatus === 'present') {
      bladderLines.push(
        `${$t('urothelial-variant-subtype-percentage')}: ${displayOptionList($t, report.bladder.variantSubtypes, translateOptions($t, optionPairs.variantSubtype))}`,
        `${$t('variant-percentage-detail')}: ${displayValue($t, report.bladder.variantPercentage)}`
      );
      if (report.bladder.variantSubtypes.includes('other')) {
        bladderLines.push(`${$t('variant-other-specify')}: ${displayValue($t, report.bladder.variantSubtypeOther)}`);
      }
    }
  }

  if (hasBladderGrading) {
    bladderLines.push('', `[${$t('grading-section')}]`);
  }

  if (hasUrothelialCarcinoma) {
    bladderLines.push(
      `${$t('urothelial-carcinoma-who-isup-grading')}: ${displayOption($t, report.bladder.urothelialGrade, translateOptions($t, optionPairs.urothelialGrade))}`,
      `${$t('growth-pattern')}: ${displayOption($t, report.bladder.growthPattern, translateOptions($t, optionPairs.growthPattern))}`
    );
  }

  if (hasSccOrAdenocarcinoma) {
    bladderLines.push(
      `${$t('scc-adenocarcinoma-grading')}: ${displayOption($t, report.bladder.sccAdenocarcinomaGrade, translateOptions($t, optionPairs.sccAdenocarcinomaGrade))}${report.bladder.sccAdenocarcinomaGrade === 'other' ? ` - ${displayValue($t, report.bladder.sccAdenocarcinomaGradeOther)}` : ''}`
    );
  }

  if (hasTumourAssessment) {
    bladderLines.push(
      '',
      `[${$t('depth-of-invasion-section')}]`,
      `${$t('depth-of-invasion')}: ${displayOption($t, report.bladder.depthOfInvasion, translateOptions($t, optionPairs.depthOfInvasion))}`,
      '',
      `[${$t('concomitant-flat-lesion-section')}]`,
      `${$t('concomitant-flat-lesion')}: ${displayOption($t, report.bladder.concomitantFlatLesion, translateOptions($t, optionPairs.concomitantFlatLesion))}`,
      '',
      `[${$t('muscularis-propria-section')}]`,
      `${$t('muscularis-propria')}: ${formatMuscularisPropria(report, $t)}`
    );
  }

  return [
    ...bladderLines,
    ...buildCommonEndingLines(report, $t, buildBladderFinalInterpretation(report, $t))
  ].join('\n');
}

export function buildBladderFinalInterpretation(report, $t) {
  const tumourType = getTumourType(report);
  if (!tumourType) {
    return '';
  }

  if (tumourType === 'benign-condition') {
    return formatDiagnosisWithDetail(
      $t('option-benign-condition'),
      report.microscopic.tumourTypeDetail,
      $t
    );
  }

  const diagnosisLines = [];
  const tumourTypeText = tumourType === 'other' && report.microscopic.tumourTypeOther
    ? report.microscopic.tumourTypeOther
    : displayOption($t, tumourType, flatTumourTypeOptions($t));

  if (tumourType === 'urothelial-carcinoma') {
    diagnosisLines.push(buildUrothelialDiagnosisLine(report, tumourTypeText, $t));
  } else {
    diagnosisLines.push(buildNonUrothelialDiagnosisLine(report, tumourTypeText, $t));
  }

  if (report.bladder.depthOfInvasion) {
    diagnosisLines.push(
      `${$t('depth-of-invasion')}${$t('final-diagnosis-detail-separator')}${displayOption($t, report.bladder.depthOfInvasion, translateOptions($t, optionPairs.depthOfInvasion))}`
    );
  }

  diagnosisLines.push(...buildMuscularisDiagnosisLines(report, $t));

  return diagnosisLines.filter(Boolean).join('\n');
}

export function buildProstateReportText(report, $t, diseaseLabel) {
  const specimens = getSelectedProstateSpecimens(report);
  return [
    `${$t('report-disease-type')}: ${displayValue($t, diseaseLabel)}`,
    '',
    `[${$t('clinical-description-section')}]`,
    `${$t('clinical-description')}: ${displayValue($t, report.clinical.description)}`,
    `${$t('dre')}: ${displayValue($t, report.clinical.dre)}`,
    `${$t('psa')}: ${displayValue($t, report.clinical.psa)}`,
    `${$t('trus')}: ${displayValue($t, report.clinical.trus)}`,
    '',
    `[${$t('type-of-specimen-section')}]`,
    ...specimens.map(specimen => `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}`),
    '',
    `[${$t('macroscopic-finding-section')}]`,
    ...specimens.map(specimen => formatProstateMacroscopicLine(specimen, $t)),
    '',
    `[${$t('microscopic-finding-section')}]`,
    ...specimens.map(specimen => formatProstateMicroscopyLine(specimen, $t)),
    '',
    `[${$t('other-finding-section')}]`,
    `${$t('high-grade-pin')}: ${formatStatusWithDetail(report.prostate.highGradePin, report.prostate.highGradePinDetail, $t)}`,
    `${$t('idc-p')}: ${formatStatusWithDetail(report.prostate.idcp, report.prostate.idcpDetail, $t)}`,
    `${$t('inflammation')}: ${displayValue($t, report.prostate.inflammation)}`,
    ...buildProstateCancerOnlyFindingLines(report, $t),
    '',
    `[${$t('final-interpretation-section')}]`,
    displayValue($t, report.finalInterpretation || buildProstateFinalInterpretation(report, $t))
  ].join('\n');
}

export function buildProstateFinalInterpretation(report, $t) {
  return buildProstateFinalInterpretationLines(report, $t).join('\n');
}

export function hasProstateCancer(report) {
  return getSelectedProstateSpecimens(report).some(specimen => specimen.result === 'acinar-adenocarcinoma');
}

export function getSelectedProstateSpecimens(report) {
  return (report.prostate.specimens || []).filter(specimen => specimen.selected);
}

function getProstateSpecimenLabel(specimen, $t) {
  return specimen.customLabel || (specimen.labelKey ? $t(specimen.labelKey) : $t('option-other'));
}

function formatProstateMacroscopicLine(specimen, $t) {
  const coreText = specimen.coreCount
    ? `${specimen.coreCount} ${$t('prostate-cores')}`
    : displayValue($t, '');
  const lengthText = specimen.coreLengths
    ? `${specimen.coreLengths} mm`
    : displayValue($t, '');
  const blockText = specimen.block
    ? `${$t('submitted-entirely-in-block')} ${specimen.block}`
    : displayValue($t, '');

  return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${coreText}, ${lengthText}. ${blockText}`;
}

function formatProstateMicroscopyLine(specimen, $t) {
  return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${formatProstateMicroscopyDiagnosis(specimen, $t)}`;
}

function formatProstateMicroscopyDiagnosis(specimen, $t) {
  if (!specimen.result) {
    return $t('not-available');
  }
  if (specimen.result === 'acinar-adenocarcinoma') {
    return formatProstateCancerDiagnosis(specimen, $t);
  }
  if (specimen.result === 'benign-stromal-tissue') {
    return $t('prostate-diagnosis-benign-stromal');
  }
  if (specimen.result === 'no-prostate-tissue') {
    return $t('prostate-diagnosis-no-prostate-tissue');
  }
  if (specimen.result === 'other') {
    return displayValue($t, specimen.resultOther);
  }
  return $t('prostate-diagnosis-benign-prostatic');
}

function formatProstateCancerDiagnosis(specimen, $t) {
  const parts = [$t('option-acinar-adenocarcinoma')];
  if (specimen.positiveCores) {
    parts.push(`${displayValue($t, specimen.positiveCores)}/${displayValue($t, specimen.coreCount)} ${$t('prostate-cores')}`);
  }
  if (specimen.tumourVolume) {
    parts.push(`${specimen.tumourVolume} ${$t('tumour-volume')}`);
  }
  if (specimen.gleasonScore) {
    parts.push(`${$t('gleason-score')}: ${formatGleasonScore(specimen.gleasonScore)}`);
  }
  const gradeGroup = specimen.gradeGroup || gradeGroupForGleason(specimen.gleasonScore);
  if (gradeGroup) {
    parts.push(`${$t('grade-group')}: ${formatGradeGroup(gradeGroup)}`);
  }
  if (specimen.cribriform === 'present') {
    parts.push($t('cribriform-present'));
  }
  return parts.join(', ');
}

function formatGleasonScore(value) {
  const scoreTotal = value.split('+').reduce((total, number) => total + Number(number || 0), 0);
  return scoreTotal ? `${scoreTotal} (${value})` : value;
}

function formatGradeGroup(value) {
  const match = String(value).match(/(\d)$/);
  return match ? match[1] : value;
}

function buildProstateCancerOnlyFindingLines(report, $t) {
  if (!hasProstateCancer(report)) {
    return [];
  }

  return [
    `${$t('periprostatic-fat-invasion')}: ${formatCancerFinding(report, 'periprostaticFatInvasion', $t)}`,
    `${$t('seminal-vesicle-invasion')}: ${formatCancerFinding(report, 'seminalVesicleInvasion', $t)}`,
    `${$t('lymphovascular-invasion')}: ${formatCancerFinding(report, 'lymphovascularInvasion', $t)}`,
    `${$t('perineural-invasion')}: ${formatCancerFinding(report, 'perineuralInvasion', $t)}`
  ];
}

function formatCancerFinding(report, key, $t) {
  return displayOption($t, report.prostate[key], translateOptions($t, optionPairs.prostateInvasionStatus));
}

function buildProstateFinalInterpretationLines(report, $t) {
  return getSelectedProstateSpecimens(report).filter(specimen => specimen.result).map(specimen => {
    if (specimen.result === 'acinar-adenocarcinoma') {
      return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${formatProstateCancerDiagnosis(specimen, $t)}.`;
    }
    if (specimen.result === 'no-prostate-tissue') {
      return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${$t('prostate-final-no-prostate-tissue')}`;
    }
    if (specimen.result === 'other') {
      return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${displayValue($t, specimen.resultOther)}.`;
    }
    const benignDiagnosis = report.prostate.highGradePin === 'not-identified'
      && report.prostate.idcp === 'not-identified'
      ? $t('prostate-final-benign')
      : $t('prostate-final-no-invasive-carcinoma');
    return `${specimen.code}. ${getProstateSpecimenLabel(specimen, $t)}: ${benignDiagnosis}`;
  });
}

function formatStatusWithDetail(value, detail, $t) {
  const status = displayOption($t, value, translateOptions($t, optionPairs.prostateFindingStatus));
  return value === 'present' && detail ? `${status} - ${detail}` : status;
}

function formatMeasurement(report, $t) {
  const measurement = report.macroscopic.measurement;
  const items = measurement.items && measurement.items.length ? measurement.items : [measurement];
  const fragmentCountText = `${$t('measurement-fragment-count')}: ${items.length}`;
  const itemTexts = items
    .map((item, index) => {
      const parts = [];
      const dimensions = [item.length, item.width, item.height].filter(Boolean);
      if (dimensions.length) {
        parts.push(`${dimensions.join(' x ')} cm`);
      }
      if (item.note) {
        parts.push(item.note);
      }
      return parts.length ? `${index + 1}. ${parts.join('; ')}` : '';
    })
    .filter(Boolean);

  return itemTexts.length ? [fragmentCountText, ...itemTexts].join('\n') : fragmentCountText;
}

function formatWeight(report, $t) {
  if (report.macroscopic.weight.status === 'measured') {
    return report.macroscopic.weight.value ? `${report.macroscopic.weight.value} g` : $t('not-available');
  }
  return displayOption($t, report.macroscopic.weight.status, translateOptions($t, optionPairs.weightStatus));
}

function getTumourType(report) {
  if (report.microscopic.tumourType !== undefined) {
    return report.microscopic.tumourType;
  }
  return report.microscopic.tumourTypes && report.microscopic.tumourTypes.length
    ? report.microscopic.tumourTypes[0]
    : '';
}

function buildUrothelialDiagnosisLine(report, tumourTypeText, $t) {
  const prefix = getInvasionPrefix(report, $t);
  const growthPatternText = getPapillaryGrowthPattern(report, $t);
  const gradeText = displayOption($t, report.bladder.urothelialGrade, translateOptions($t, optionPairs.urothelialGrade));
  const diagnosis = [prefix, growthPatternText, tumourTypeText]
    .filter(Boolean)
    .join($t('final-diagnosis-term-separator'));

  return appendGrade(diagnosis, report.bladder.urothelialGrade ? gradeText : '', $t);
}

function buildNonUrothelialDiagnosisLine(report, tumourTypeText, $t) {
  if (report.microscopic.tumourType === 'other') {
    return tumourTypeText;
  }

  const gradeText = displayOption($t, report.bladder.sccAdenocarcinomaGrade, translateOptions($t, optionPairs.sccAdenocarcinomaGrade));
  const gradeDetail = report.bladder.sccAdenocarcinomaGrade === 'other'
    ? report.bladder.sccAdenocarcinomaGradeOther
    : gradeText;

  return appendGrade(tumourTypeText, report.bladder.sccAdenocarcinomaGrade ? gradeDetail : '', $t);
}

function getInvasionPrefix(report, $t) {
  if (report.bladder.depthOfInvasion === 'ta-no-lamina-propria-invasion') {
    return $t('final-diagnosis-non-infiltrating');
  }
  if ([
    't1-focal-lamina-propria-invasion',
    't1-extensive-lamina-propria-invasion',
    't2-muscularis-propria-invasion'
  ].includes(report.bladder.depthOfInvasion)) {
    return $t('final-diagnosis-invasive');
  }
  if (report.bladder.growthPattern === 'infiltrating' ||
    report.bladder.growthPattern === 'papillary-and-infiltrating') {
    return $t('final-diagnosis-invasive');
  }
  return '';
}

function getPapillaryGrowthPattern(report, $t) {
  return report.bladder.growthPattern === 'papillary' ||
    report.bladder.growthPattern === 'papillary-and-infiltrating'
    ? $t('option-papillary')
    : '';
}

function appendGrade(diagnosis, gradeText, $t) {
  return gradeText ? `${diagnosis}${$t('final-diagnosis-separator')}${gradeText}` : diagnosis;
}

function buildMuscularisDiagnosisLines(report, $t) {
  const muscularisPropria = report.bladder.muscularisPropria;
  if (!muscularisPropria || !muscularisPropria.presence) {
    return [];
  }

  if (muscularisPropria.presence === 'absent') {
    return [$t('final-diagnosis-muscularis-not-included')];
  }

  const lines = [$t('final-diagnosis-muscularis-included')];
  if (muscularisPropria.invasion === 'involved') {
    lines.push($t('final-diagnosis-muscularis-invasion'));
  } else if (muscularisPropria.invasion === 'not-involved') {
    lines.push($t('final-diagnosis-muscularis-no-invasion'));
  }
  return lines;
}

function formatDiagnosisWithDetail(label, detail, $t) {
  return detail ? `${label}${$t('final-diagnosis-detail-separator')}${detail}` : label;
}

function formatMuscularisPropria(report, $t) {
  const value = report.bladder.muscularisPropria;
  if (!value) {
    return $t('not-available');
  }
  if (typeof value === 'string') {
    return displayOption($t, value, translateOptions($t, optionPairs.muscularisPropria));
  }
  if (value.presence === 'absent') {
    return $t('option-absent');
  }
  if (value.presence === 'present') {
    const invasion = value.invasion === 'involved'
      ? $t('option-inv')
      : value.invasion === 'not-involved'
        ? $t('option-no-inv')
        : $t('not-available');
    return `${$t('option-present')} - ${invasion}`;
  }
  return $t('not-available');
}
